// ============================================================
// HELIX — FROZEN 2026-05-20 by Bryan K. Short
// Do not modify visually until a planned redesign happens.
// Mechanical fixes (data binding, perf, prop changes) ARE allowed.
// Visual changes (geometry, palette, dimensions, motion) are NOT.
// ============================================================

/**
 * OperationalHelix.tsx — DNA Account Intelligence Surface
 *
 * User-facing brand: DNA
 * Internal name: OperationalHelix (component name preserved for imports)
 *
 * Three-zone layout per Bryan's locked spec (2026-05-06):
 *
 *   Zone 1  — 3D Helix (visual surface, full marketing-site look)
 *   Zone 2  — CP Legend (beside helix on desktop, below on mobile)
 *   Zone 3  — Q&A Panel (workspace, inline below — NOT a modal)
 *
 * Modal-exit rule: X button · Esc key · backdrop click · Back/Home button
 *   (applies to the DNA tab itself when reached as a sub-screen;
 *    the Q&A panel is inline, no exit needed for the panel)
 *
 * Two-way sync:
 *   Orb click → legend highlights + Q&A opens
 *   Legend click → helix orients + Q&A opens
 *   Answer → orb color updates + legend score updates + composite recomputes
 *   After answer save → helix returns to home rest (smooth ~1s)
 *
 * Data: localStorage `aip:helix:{accountId}` — account-scoped, persists answers
 * Audit: localStorage `aip:helix:audit:{accountId}`
 * Event: `aip:answer-saved` dispatched on every answer save (cross-view sync)
 *
 * Brand: Black #0B0B0F · Red #DC2626 · AIP bg #FAFAF7 · Helix dark #0A0A0C
 * Roles: bdr/ae/manager/executive — never "rep"
 * M-PACT with hyphen always
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
// lucide-react Tree-toggle icons (LayoutList, GitBranch) removed 2026-05-22 (D-075)
import DnaHelix3D from "@/components/helix/DnaHelix3D";
import CpLegend from "@/components/helix/CpLegend";
import QaPanel from "@/components/helix/QaPanel";
import AnswerEntryModal from "@/components/helix/AnswerEntryModal";
import ScoreHeader from "@/components/helix/ScoreHeader";
import {
  HELIX_CHECKPOINTS,
  type CheckpointStatus,
  type CheckpointStatusMap,
  getScoreBucket,
} from "@/lib/helix-checkpoints";
import {
  ALL_QUESTIONS,
  getActiveQuestions,
} from "@shared/questions";

// ─── Locked 5-point graded scale (matches calculator v2.2 exactly) ────────────

const GRADED_OPTIONS_TREE = [
  { value: "4", anchor: "Strong",  label: "Strong",  borderColor: "#16A34A" },
  { value: "3", anchor: "Mostly",  label: "Mostly",  borderColor: "#16A34A" },
  { value: "2", anchor: "Partial", label: "Partial", borderColor: "#EAB308" },
  { value: "1", anchor: "Weak",    label: "Weak",    borderColor: "#EA580C" },
  { value: "0", anchor: "Absent",  label: "Absent",  borderColor: "#DC2626" },
] as const;
import { getSession, supabase } from "@/lib/session";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface CpAnswer {
  selectedOption: string | null;
  notes: string;
  updatedAt: string;
  updatedBy: string;
  // TICKET-4: evidence fields
  evidenceType?: "text" | "url" | "file" | null;
  evidenceUrl?: string | null;
  evidenceFileName?: string | null;
  isAssumption?: boolean;
}

export type AnswerStore = Record<string, CpAnswer>;

interface AuditEntry {
  questionKey: string;
  oldOption: string | null;
  newOption: string | null;
  notes: string;
  updatedAt: string;
  updatedBy: string;
}

// ViewMode type removed 2026-05-22 (D-075): only Helix view remains.

export interface OperationalHelixProps {
  accountId: string;
  accountName?: string;
  industry?: string;
  initialAnswers?: AnswerStore;
  // D-037: external focus signal — when this changes to a non-null CP number,
  // the Helix selects that CP and opens the Q&A panel scoped to it. Used by
  // AccountScorecard's CP-card click handler so the user lands on CP1's data
  // (not the generic "Select a checkpoint" placeholder) in one click.
  focusCp?: number | null;
  /** When true, all answer-entry controls are disabled and a read-only badge
   *  is shown. Orb/legend navigation remains fully interactive. Defaults to false. */
  readOnly?: boolean;
  /** W44.2: When true, suppress the "Opportunity DNA" title/topbar — used when
   *  OperationalHelix is embedded as a panel inside another page (e.g. Tower).
   *  The composite ring, account label, and M-PACT sub-label are hidden. */
  hideHeader?: boolean;
  /** W45.3: Optional callback fired whenever the user clicks a CP node.
   *  Enables cross-panel highlight — caller can forward the value to
   *  SelectedCpContext.setSelectedCp. Receives null when the selection is cleared. */
  onCpSelect?: (cp: number | null) => void;
  /**
   * W61: controls CP ordering along the vertical axis of the 3D helix.
   * 'top-down' (default): CP1 at top, CP10 at bottom (Galaxy / legacy).
   * 'bottom-up': CP1 at waterline, CP10 at roof (Tower canon, /opportunity-dna).
   */
  helixOrientation?: "bottom-up" | "top-down";
}

// ─── LocalStorage ───────────────────────────────────────────────────────────────

function lsKey(id: string) {
  return `aip:helix:${id}`;
}
function loadAnswers(id: string): AnswerStore {
  try {
    const raw = localStorage.getItem(lsKey(id));
    return raw ? (JSON.parse(raw) as AnswerStore) : {};
  } catch {
    return {};
  }
}
function saveAnswers(id: string, answers: AnswerStore) {
  try {
    localStorage.setItem(lsKey(id), JSON.stringify(answers));
  } catch {}
}
function appendAuditEntry(id: string, entry: AuditEntry) {
  try {
    const log: AuditEntry[] = JSON.parse(
      localStorage.getItem(`aip:helix:audit:${id}`) ?? "[]"
    );
    log.unshift(entry);
    localStorage.setItem(
      `aip:helix:audit:${id}`,
      JSON.stringify(log.slice(0, 500))
    );
  } catch {}
}

// ─── Score helpers ──────────────────────────────────────────────────────────────

/**
 * Compute CP score using the locked 5-point graded scale math.
 *
 * selectedOption stores the raw point value as a string: "4", "3", "2", "1", "0".
 * null = skip (excluded from numerator AND denominator).
 *
 * per_cp_score = round( (sum_points_answered / (4 × count_answered)) × 100 )
 * Returns pct = -1 when zero questions have been answered (renders as "—").
 */
/**
 * Resolve a stored selectedOption to a numeric point value.
 *
 * Two storage formats are supported in account_answers.selected_option:
 *  1. Graded 5-point scale: "0".."4" — the numeric string IS the point value.
 *  2. Bespoke option key: "yes-named" / "no" / "complete" / "unclear" — used
 *     by Q9.3.A-D (paper-process sub-questions). Resolved via QuestionDef.options.
 *
 * Returns null when the stored value matches neither format (treated as skip).
 */


function computeCpScore(
  cp: number,
  answers: AnswerStore,
  _industry: string | null | undefined
): { raw: number; max: number; pct: number } {
  const cpQs = ALL_QUESTIONS.filter((q) => q.cp === cp);

  let sumPoints = 0;
  let sumMax = 0;
  let countAnswered = 0;

  for (const q of cpQs) {
    const opt = answers[q.key]?.selectedOption;
    if (opt === null || opt === undefined) continue; // skip excluded
    const pts = resolveOptionPoints(q, opt);
    if (pts !== null) {
      sumPoints += pts;
      sumMax += Math.max(...q.options.map((o) => o.points));
      countAnswered += 1;
    }
  }

  if (countAnswered === 0 || sumMax === 0) return { raw: 0, max: 0, pct: -1 };
  // Clamp pct to [0, 100] — defensive against any future scoring overflow.
  const pct = Math.max(0, Math.min(100, Math.round((sumPoints / sumMax) * 100)));
  return { raw: sumPoints, max: sumMax, pct };
}

/**
 * Resolve a stored selectedOption to a numeric point value.
 *
 * Two storage formats are supported in account_answers.selected_option:
 *  1. Graded 5-point scale: "0".."4" — the numeric string IS the point value.
 *  2. Bespoke option key: "yes-named" / "no" / "complete" / "unclear" — used
 *     by Q9.3.A-D (paper-process sub-questions). Resolved via QuestionDef.options.
 *
 * Returns null when the stored value matches neither format (treated as skip).
 */
function resolveOptionPoints(
  q: typeof ALL_QUESTIONS[number],
  storedValue: string,
): number | null {
  // Cap returned points to the question's maximum so stale legacy values
  // (e.g. "5" or "7" stored on a 0..4 scale) cannot push the composite over 100.
  const maxPoints = Math.max(...q.options.map((o) => o.points));
  const asNumber = parseInt(storedValue, 10);
  if (!Number.isNaN(asNumber) && /^[0-9]+$/.test(storedValue)) {
    return Math.max(0, Math.min(asNumber, maxPoints));
  }
  const opt = q.options.find((o) => o.key === storedValue);
  if (!opt) return null;
  return Math.max(0, Math.min(opt.points, maxPoints));
}

/**
 * Compute composite score — Wave 1 weighted-CP formula (2026-05-21).
 *
 * Mirrors the server-side recompute_account_score() function exactly:
 *   For each CP 1..10:
 *     cp_pct = SUM(points answered) / SUM(max-of-options for answered questions) * 100
 *     If no questions answered in CP, cp_pct = null (contributes 0 to numerator).
 *   composite = SUM(cp_pct * 0.10) / 1.0
 *
 * Equal 0.10 weight per CP. Empty CPs consume their weight share, so partial
 * coverage is honestly reflected: an account with only CP1 answered at 100%
 * scores 10 (not 100), which matches the server.
 *
 * Wave 2 will swap equal weights for canonical dimension weights without
 * touching this plumbing.
 */
function computeCompositeScore(answers: AnswerStore): number {
  const CP_WEIGHT = 0.1; // equal weighting Wave 1
  const TOTAL_WEIGHT = 1.0; // 10 * 0.10

  let weightedSum = 0;

  for (let cp = 1; cp <= 10; cp++) {
    const cpQs = ALL_QUESTIONS.filter((q) => q.cp === cp);
    let sumPoints = 0;
    let sumMax = 0;

    for (const q of cpQs) {
      const opt = answers[q.key]?.selectedOption;
      if (opt === null || opt === undefined) continue;
      const pts = resolveOptionPoints(q, opt);
      if (pts === null) continue;
      const qMax = Math.max(...q.options.map((o) => o.points));
      sumPoints += pts;
      sumMax += qMax;
    }

    if (sumMax > 0) {
      const cpPct = (sumPoints / sumMax) * 100;
      weightedSum += cpPct * CP_WEIGHT;
    }
    // else: CP has no answered questions — contributes 0 to numerator,
    // but its 0.10 weight remains in the denominator (TOTAL_WEIGHT).
  }

  const composite = weightedSum / TOTAL_WEIGHT;
  return Math.max(0, Math.min(100, Math.round(composite)));
}

function deriveStatus(pct: number): CheckpointStatus {
  if (pct < 0) return "open"; // -1 = no answered questions yet
  if (pct >= 70) return "cleared";
  if (pct >= 40) return "partial";
  return "open";
}

// ─── Score color helpers ────────────────────────────────────────────────────────

function scoreColor(pct: number | null | undefined): string {
  if (pct == null || pct <= 0) return "#6B7280";
  if (pct >= 80) return "#16A34A";
  if (pct >= 60) return "#EAB308";
  if (pct >= 40) return "#EA580C";
  return "#DC2626";
}
function scoreBg(pct: number | null | undefined): string {
  if (pct == null || pct <= 0) return "rgba(107,114,128,0.12)";
  if (pct >= 80) return "rgba(22,163,74,0.12)";
  if (pct >= 60) return "rgba(234,179,8,0.12)";
  if (pct >= 40) return "rgba(234,88,12,0.12)";
  return "rgba(220,38,38,0.12)";
}

// ─── CompositeRing ──────────────────────────────────────────────────────────────

function CompositeRing({ score }: { score: number }) {
  const radius = 36;
  const circ = 2 * Math.PI * radius;
  const pct = Math.min(100, Math.max(0, score));
  const offset = circ - (pct / 100) * circ;
  const color = scoreColor(pct);
  const bucket = getScoreBucket(pct);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ position: "relative", width: 88, height: 88, flexShrink: 0 }}>
        <svg width={88} height={88} viewBox="0 0 88 88" style={{ transform: "rotate(-90deg)" }}>
          <circle cx={44} cy={44} r={radius} fill="none" stroke="rgba(107,114,128,0.18)" strokeWidth={8} />
          <circle
            cx={44}
            cy={44}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={8}
            strokeLinecap="round"
            strokeDasharray={`${circ}`}
            strokeDashoffset={offset}
            style={{
              transition: "stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1), stroke 0.4s ease",
            }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {score}
          </span>
          <span style={{ fontSize: 9, color: "#8E8E94", fontWeight: 600, letterSpacing: "0.06em", marginTop: 1 }}>
            /100
          </span>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#6B6B70", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 4 }}>
          Composite score
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#EFEFEE", lineHeight: 1 }}>
          {score}
          <span style={{ fontSize: 14, fontWeight: 500, color: "#6B6B70" }}>/100</span>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            marginTop: 6,
            padding: "2px 10px",
            borderRadius: 20,
            background: scoreBg(pct),
            border: `1px solid ${color}33`,
            fontSize: 11,
            fontWeight: 700,
            color,
            letterSpacing: "0.05em",
          }}
        >
          {bucket}
        </div>
      </div>
    </div>
  );
}

// ─── QuestionTree (compact accordion mode) ──────────────────────────────────────

interface TreeModeProps {
  answers: AnswerStore;
  industry: string | null | undefined;
  onSaveAnswer: (key: string, opt: string | null, notes: string) => void;
  cpScores: Array<{ cp: number; pct: number }>;
}

function ScorePill({ pct }: { pct: number }) {
  const color = scoreColor(pct);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 12,
        background: scoreBg(pct),
        border: `1px solid ${color}44`,
        fontSize: 10,
        fontWeight: 700,
        color,
        whiteSpace: "nowrap",
        transition: "background 0.3s, color 0.3s",
      }}
    >
      {pct}%
    </span>
  );
}

import { ChevronDown, ChevronUp } from "lucide-react";

function QuestionTreeMode({ answers, industry, onSaveAnswer, cpScores }: TreeModeProps) {
  const [openCps, setOpenCps] = useState<Set<number>>(new Set([1]));
  const [drafts, setDrafts] = useState<
    Record<string, { selectedOption: string | null; notes: string }>
  >({});

  useEffect(() => {
    const init: typeof drafts = {};
    ALL_QUESTIONS.forEach((q) => {
      const ex = answers[q.key];
      init[q.key] = {
        selectedOption: ex?.selectedOption ?? null,
        notes: ex?.notes ?? "",
      };
    });
    setDrafts(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      ALL_QUESTIONS.forEach((q) => {
        const ex = answers[q.key];
        if (ex && next[q.key] && ex.selectedOption !== next[q.key].selectedOption) {
          next[q.key] = { ...next[q.key], selectedOption: ex.selectedOption };
        }
      });
      return next;
    });
  }, [answers]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {HELIX_CHECKPOINTS.map((cp) => {
        const cpQ = ALL_QUESTIONS.filter((q) => q.cp === cp.number);
        const isOpen = openCps.has(cp.number);
        const score = cpScores.find((s) => s.cp === cp.number);
        const rawPct = score?.pct ?? -1;
        const pct = rawPct < 0 ? 0 : rawPct;
        const hasScore = rawPct >= 0;
        const color = hasScore ? scoreColor(pct) : "#6B6B70";
        const answered = cpQ.filter((q) => {
          const opt = answers[q.key]?.selectedOption;
          return opt !== null && opt !== undefined;
        }).length;

        return (
          <div
            key={cp.id}
            style={{
              background: "rgba(255,255,255,0.03)",
              borderRadius: 10,
              border: "1px solid rgba(107,114,128,0.1)",
              overflow: "hidden",
            }}
          >
            <button
              onClick={() =>
                setOpenCps((prev) => {
                  const next = new Set(prev);
                  isOpen ? next.delete(cp.number) : next.add(cp.number);
                  return next;
                })
              }
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "11px 14px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: scoreBg(pct),
                  border: `2px solid ${color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 800,
                  color,
                  flexShrink: 0,
                }}
              >
                {cp.number}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#EFEFEE" }}>
                  {cp.fullName}
                </div>
                <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 1 }}>
                  {answered}/{cpQ.length} answered
                </div>
              </div>
              {hasScore ? <ScorePill pct={pct} /> : (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#6B6B70" }}>—</span>
              )}
              {isOpen ? (
                <ChevronUp size={13} color="#6B6B70" />
              ) : (
                <ChevronDown size={13} color="#6B6B70" />
              )}
            </button>

            {isOpen && (
              <div
                style={{
                  borderTop: "1px solid rgba(107,114,128,0.08)",
                  padding: "12px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {cpQ.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#6B6B70" }}>
                    No questions for this checkpoint.
                  </div>
                ) : (
                  cpQ.map((q, idx) => {
                    const draft = drafts[q.key] ?? { selectedOption: null, notes: "" };
                    return (
                      <div
                        key={q.key}
                        style={{
                          background: "rgba(255,255,255,0.02)",
                          borderRadius: 8,
                          border: "1px solid rgba(107,114,128,0.08)",
                          padding: "11px 13px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#C9C9CC",
                            marginBottom: 7,
                            lineHeight: 1.45,
                          }}
                        >
                          <span style={{ color: "#525258", marginRight: 5, fontSize: 10 }}>
                            Q{idx + 1}
                          </span>
                          {q.label}
                        </div>
                        {/* 5-point graded scale (tree compact view) */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                          {GRADED_OPTIONS_TREE.map((opt) => {
                            const isSel = draft.selectedOption === opt.value;
                            return (
                              <button
                                key={opt.value}
                                onClick={() => {
                                  const newOpt = draft.selectedOption === opt.value ? null : opt.value;
                                  setDrafts((prev) => ({
                                    ...prev,
                                    [q.key]: { ...prev[q.key], selectedOption: newOpt },
                                  }));
                                  onSaveAnswer(q.key, newOpt, drafts[q.key]?.notes ?? "");
                                }}
                                style={{
                                  padding: "3px 9px",
                                  borderRadius: 14,
                                  border: isSel ? `1px solid ${opt.borderColor}55` : "1px solid rgba(107,114,128,0.14)",
                                  borderLeft: `3px solid ${opt.borderColor}`,
                                  background: isSel ? `${opt.borderColor}18` : "rgba(255,255,255,0.02)",
                                  color: isSel ? "#EFEFEE" : "#8E8E94",
                                  fontSize: 11,
                                  fontWeight: isSel ? 700 : 500,
                                  cursor: "pointer",
                                  transition: "all 0.12s",
                                }}
                              >
                                {opt.anchor}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => {
                              setDrafts((prev) => ({
                                ...prev,
                                [q.key]: { ...prev[q.key], selectedOption: null },
                              }));
                              onSaveAnswer(q.key, null, drafts[q.key]?.notes ?? "");
                            }}
                            style={{
                              padding: "3px 9px",
                              borderRadius: 14,
                              border: "1px solid rgba(107,114,128,0.12)",
                              background: "transparent",
                              color: "#525258",
                              fontSize: 10,
                              fontWeight: 500,
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            Skip
                          </button>
                        </div>
                        <textarea
                          value={draft.notes}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [q.key]: { ...prev[q.key], notes: e.target.value },
                            }))
                          }
                          onBlur={() =>
                            onSaveAnswer(q.key, drafts[q.key]?.selectedOption ?? null, drafts[q.key]?.notes ?? "")
                          }
                          placeholder="Notes..."
                          rows={1}
                          style={{
                            marginTop: 7,
                            width: "100%",
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(107,114,128,0.1)",
                            borderRadius: 5,
                            color: "#8E8E94",
                            fontSize: 11,
                            padding: "5px 8px",
                            resize: "vertical",
                            outline: "none",
                            fontFamily: "inherit",
                            lineHeight: 1.4,
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── W15: CpTapeRow — tape-recorder CP button bar ─────────────────────────────
// 10 equal-width buttons. Color-coded by score. 5-color palette (locked 2026-05-23).

// ─── W33 fix: answerColor / answerLabel — missing from CpReadPanel, caused
// crash on every CP tap in readOnly mode on /opportunity-dna.
function answerColor(opt: string | null): string {
  if (opt === null || opt === undefined) return "#6B7280";
  const n = parseInt(opt, 10);
  if (!Number.isNaN(n)) {
    if (n >= 4) return "#16A34A";
    if (n >= 3) return "#16A34A";
    if (n >= 2) return "#EAB308";
    if (n >= 1) return "#EA580C";
    return "#DC2626";
  }
  // bespoke keys
  if (opt === "yes-named" || opt === "complete") return "#16A34A";
  if (opt === "unclear") return "#EAB308";
  return "#DC2626";
}

function answerLabel(opt: string | null): string {
  if (opt === null || opt === undefined) return "Not answered";
  const n = parseInt(opt, 10);
  if (!Number.isNaN(n)) {
    const labels: Record<number, string> = { 4: "Strong", 3: "Mostly", 2: "Partial", 1: "Weak", 0: "Absent" };
    return labels[n] ?? opt;
  }
  const bespoke: Record<string, string> = {
    "yes-named": "Yes — Named",
    "complete":  "Complete",
    "unclear":   "Unclear",
    "no":        "No",
  };
  return bespoke[opt] ?? opt;
}

function cpStatusColor(pct: number): { bg: string; border: string; text: string } {
  if (pct < 0) return { bg: "rgba(107,107,112,0.15)", border: "rgba(107,107,112,0.4)", text: "#8E8E94" };
  if (pct >= 70) return { bg: "rgba(22,163,74,0.15)", border: "rgba(22,163,74,0.5)", text: "#16A34A" };
  if (pct >= 40) return { bg: "rgba(234,179,8,0.15)", border: "rgba(234,179,8,0.5)", text: "#CA8A04" };
  return { bg: "rgba(220,38,38,0.12)", border: "rgba(220,38,38,0.5)", text: "#DC2626" };
}

interface CpTapeRowProps {
  cpScores: Array<{ cp: number; pct: number }>;
  selectedCp: number | null;
  onSelect: (cp: number) => void;
}

function CpTapeRow({ cpScores, selectedCp, onSelect }: CpTapeRowProps) {
  return (
    <div
      style={{
        padding: "12px 24px 16px",
        borderTop: "1px solid rgba(107,114,128,0.1)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#525258",
          marginBottom: 8,
        }}
      >
        CHECKPOINTS — tap to view health
      </div>
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        {HELIX_CHECKPOINTS.map((cp) => {
          const score = cpScores.find((s) => s.cp === cp.number);
          const pct = score?.pct ?? -1;
          const colors = cpStatusColor(pct);
          const isSelected = selectedCp === cp.number;
          return (
            <button
              key={cp.id}
              onClick={() => onSelect(cp.number)}
              title={`${cp.fullName} — ${pct >= 0 ? pct + "%" : "No answers yet"}`}
              style={{
                flex: "1 1 60px",
                minWidth: 52,
                maxWidth: 80,
                padding: "7px 4px 6px",
                borderRadius: 8,
                border: isSelected
                  ? `2px solid ${colors.text}`
                  : `1px solid ${colors.border}`,
                background: isSelected ? colors.bg : "rgba(255,255,255,0.03)",
                color: isSelected ? colors.text : "#8E8E94",
                fontSize: 10,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.12s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                outline: "none",
                boxShadow: isSelected ? `0 0 0 1px ${colors.text}33` : "none",
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 800, color: isSelected ? colors.text : "#EFEFEE" }}>
                CP{cp.number}
              </span>
              <span style={{ fontSize: 9, color: colors.text, fontWeight: 600 }}>
                {pct >= 0 ? `${pct}%` : "\u2014"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── W15: CpReadPanel — inline right panel for readOnly mode ──────────────────
// Populated by tapping a CP tape-recorder button. No editing controls.

interface CpReadPanelProps {
  selectedCp: number | null;
  answers: AnswerStore;
  cpScores: Array<{ cp: number; pct: number; raw: number; max: number }>;
  industry?: string;
  /** W66.W: callback to persist a saved answer (mirrors saveAnswer in parent) */
  onSaveAnswer?: (questionKey: string, selectedOption: string | null, notes: string) => void;
  /** W66.W: user identifier for AnswerEntryModal audit trail */
  userId?: string;
}

function CpReadPanel({ selectedCp, answers, cpScores, industry, onSaveAnswer, userId }: CpReadPanelProps) {
  const cp = selectedCp !== null ? HELIX_CHECKPOINTS.find((c) => c.number === selectedCp) : null;
  // W43.11: Apply industry filter to exclude industry-segmented questions from wrong sectors.
  const cpQs = selectedCp !== null ? getActiveQuestions({ industry: industry ?? null, customerPath: null }).filter((q) => q.cp === selectedCp) : [];
  const score = selectedCp !== null ? cpScores.find((s) => s.cp === selectedCp) : null;
  const pct = score?.pct ?? -1;
  const colors = cpStatusColor(pct);
  const answeredCount = cpQs.filter((q) => {
    const opt = answers[q.key]?.selectedOption;
    return opt !== null && opt !== undefined;
  }).length;

  // W66.W: track which question card has the inline AnswerEntryModal open.
  // null = all collapsed; string = question key of the open card.
  const [openQuestionKey, setOpenQuestionKey] = useState<string | null>(null);
  // W66.W: toast for save error feedback (rev on error).
  const [errorToast, setErrorToast] = useState<string | null>(null);

  // W66.W: listen for Supabase write failures to show a small error toast.
  useEffect(() => {
    function onSaveError(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (detail?.accountId !== undefined) {
        setErrorToast("Save failed — please try again.");
        setTimeout(() => setErrorToast(null), 4000);
      }
    }
    window.addEventListener("aip:answer-save-error", onSaveError);
    return () => window.removeEventListener("aip:answer-save-error", onSaveError);
  }, []);

  if (selectedCp === null || !cp) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 20px",
          gap: 12,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "#6B7280" }}>
          Checkpoint Health
        </div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5, maxWidth: 220 }}>
          Tap a checkpoint in the left panel to view its health, score, and answers.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid #E5E7EB" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>
          Checkpoint
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>
          {cp.fullName}
        </div>
        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4, lineHeight: 1.4 }}>
          {cp.description}
        </div>
      </div>
      <div
        style={{
          margin: "12px 16px 0",
          padding: "10px 12px",
          background: colors.bg,
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#6B7280", letterSpacing: "0.14em", textTransform: "uppercase" }}>Score</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: colors.text, lineHeight: 1 }}>
            {pct >= 0 ? `${pct}%` : "\u2014"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#6B7280", letterSpacing: "0.14em", textTransform: "uppercase" }}>Answers</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
            {answeredCount}/{cpQs.length}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {cpQs.length === 0 && <div style={{ fontSize: 12, color: "#6B7280" }}>No questions for this checkpoint.</div>}
        {cpQs.map((q, idx) => {
          const ans = answers[q.key];
          const opt = ans?.selectedOption ?? null;
          const aColor = answerColor(opt);
          const isOpen = openQuestionKey === q.key;
          const canEdit = typeof onSaveAnswer === "function";
          return (
            <div
              key={q.key}
              style={{
                background: isOpen ? "#FFFFFF" : "#F9FAFB",
                borderRadius: 8,
                border: isOpen ? "1px solid #DC262644" : "1px solid #E5E7EB",
                padding: "10px 12px",
                cursor: canEdit ? "pointer" : "default",
                transition: "border-color 0.15s, background 0.15s",
              }}
              onClick={canEdit && !isOpen ? () => setOpenQuestionKey(q.key) : undefined}
              role={canEdit ? "button" : undefined}
              tabIndex={canEdit && !isOpen ? 0 : undefined}
              onKeyDown={canEdit && !isOpen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenQuestionKey(q.key); } } : undefined}
              aria-expanded={isOpen}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", marginBottom: 4, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Q{idx + 1}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", lineHeight: 1.45, marginBottom: 6 }}>
                {q.label}
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: aColor, background: `${aColor}18`, border: `1px solid ${aColor}44`, borderRadius: 10, padding: "2px 8px" }}>
                {answerLabel(opt)}
              </span>
              {ans?.notes && !isOpen && (
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 5, lineHeight: 1.4, fontStyle: "italic" }}>
                  {ans.notes}
                </div>
              )}
              {/* W66.W: inline AnswerEntryModal — expands in place when card tapped */}
              {isOpen && canEdit && (
                <AnswerEntryModal
                  questionKey={q.key}
                  questionLabel={q.label}
                  existingAnswer={ans ? {
                    value: opt,
                    is_evidence: !(ans.isAssumption !== false),
                    notes: ans.notes ?? "",
                  } : undefined}
                  userId={userId ?? "user"}
                  onCancel={() => setOpenQuestionKey(null)}
                  onSave={(entry) => {
                    // Optimistic UI: close modal immediately
                    setOpenQuestionKey(null);
                    // Persist via parent saveAnswer
                    onSaveAnswer(
                      entry.question_key,
                      entry.points !== null ? String(entry.points) : null,
                      entry.answer_notes,
                    );
                  }}
                />
              )}
            </div>
          );
        })}
        {/* W66.W: error toast shown on save failure */}
        {errorToast && (
          <div
            role="alert"
            style={{
              padding: "8px 12px",
              borderRadius: 7,
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              color: "#DC2626",
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            {errorToast}
          </div>
        )}
      </div>
      <div style={{ padding: "10px 16px 14px", borderTop: "1px solid #E5E7EB", fontSize: 10, color: "#6B7280", lineHeight: 1.5 }}>
        Tap any question to answer or edit.
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────────

export default function OperationalHelix({
  accountId,
  accountName,
  industry,
  initialAnswers,
  focusCp,
  // CANON 2026-05-27: helix surfaces are VIEW-ONLY by default. Editing answers
  // happens in the account-level question flow, never through the helix. To opt
  // into edits, a caller must explicitly pass readOnly={false} — currently no
  // production caller does, and per Bryan, none should.
  readOnly = true,
  // W44.2: suppress topbar title when embedded in Tower or other host pages
  hideHeader = false,
  // W45.3: cross-panel CP highlight callback
  onCpSelect,
  // W61: CP vertical ordering — 'bottom-up' for /opportunity-dna (Tower canon)
  helixOrientation = "top-down",
}: OperationalHelixProps) {
  const [answers, setAnswers] = useState<AnswerStore>(() => {
    const stored = loadAnswers(accountId);
    return Object.keys(stored).length > 0 ? stored : (initialAnswers ?? {});
  });
  // viewMode removed 2026-05-22 (D-075): Tree toggle eliminated, Helix is the only view.
  const [selectedCp, setSelectedCp] = useState<number | null>(
    typeof focusCp === "number" ? focusCp : null,
  );

  // Re-hydrate on account switch
  useEffect(() => {
    const stored = loadAnswers(accountId);
    setAnswers(Object.keys(stored).length > 0 ? stored : (initialAnswers ?? {}));
    setSelectedCp(typeof focusCp === "number" ? focusCp : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // D-037: respond to external focus changes (CP-card click in AccountScorecard).
  // When focusCp transitions to a non-null number, select that CP so the Q&A
  // panel opens with its data instead of the generic placeholder.
  useEffect(() => {
    if (typeof focusCp === "number") {
      setSelectedCp(focusCp);
    }
  }, [focusCp]);

  // DB hydration: if localStorage is empty for this account, pull answers from
  // account_answers in Supabase and seed local state. This is the bridge
  // between server-persisted answers (entered via AnswerEntryModal, account
  // detail forms, or pre-migration data) and the Helix view's local store.
  // Added 2026-05-15 to fix "answered checkpoints with no composite" bug.
  useEffect(() => {
    const accountIdNum = parseInt(accountId, 10);
    if (Number.isNaN(accountIdNum)) return;
    const stored = loadAnswers(accountId);
    if (Object.keys(stored).length > 0) return; // already hydrated
    let cancelled = false;
    supabase()
      .from("account_answers")
      // TICKET-4: include evidence columns in hydration
      .select("question_key, selected_option, answer_notes, updated_at, evidence_type, evidence_url, evidence_file_name, is_assumption")
      .eq("account_id", accountIdNum)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data || data.length === 0) return;
        const hydrated: AnswerStore = {};
        for (const row of data) {
          if (!row.question_key) continue;
          hydrated[row.question_key] = {
            selectedOption: row.selected_option ?? null,
            notes: row.answer_notes ?? "",
            updatedAt: row.updated_at ?? new Date().toISOString(),
            updatedBy: "db-hydrated",
            // TICKET-4
            evidenceType: (row.evidence_type as "text" | "url" | "file" | null) ?? null,
            evidenceUrl: row.evidence_url ?? null,
            evidenceFileName: row.evidence_file_name ?? null,
            isAssumption: !!row.is_assumption,
          };
        }
        if (Object.keys(hydrated).length > 0) {
          setAnswers(hydrated);
          saveAnswers(accountId, hydrated);
        }
      });
    return () => { cancelled = true; };
  }, [accountId]);

  // Persist on every change
  useEffect(() => {
    saveAnswers(accountId, answers);
  }, [accountId, answers]);

  // CP scores
  const cpScores = useMemo(
    () =>
      HELIX_CHECKPOINTS.map((cp) => {
        const s = computeCpScore(cp.number, answers, industry);
        return { cp: cp.number, ...s };
      }),
    [answers, industry]
  );

  // CP scores as a keyed map for DnaHelix3D and CpLegend
  const cpScoreMap = useMemo(() => {
    const m: Record<string, number> = {};
    cpScores.forEach(({ cp, pct }) => {
      m[`cp${cp}`] = pct;
    });
    return m;
  }, [cpScores]);

  // Helix visual statuses
  const checkpointStatuses = useMemo<CheckpointStatusMap>(() => {
    const m: CheckpointStatusMap = {};
    cpScores.forEach(({ cp, pct }) => {
      m[`cp${cp}`] = deriveStatus(pct);
    });
    return m;
  }, [cpScores]);

  const compositeScore = useMemo(
    () => computeCompositeScore(answers),
    [answers]
  );

  // TICKET-4: count answers without evidence (is_assumption=true, evidenceType=null)
  // Used by ScoreHeader to show "Score is capped because N answers lack evidence."
  const unevidencedAssumptionCount = useMemo(() => {
    return Object.values(answers).filter(
      (a) => a.isAssumption !== false && !a.evidenceType,
    ).length;
  }, [answers]);

  // Save answer — updates local state, audit log, fires cross-view event, AND
  // persists to Supabase `account_answers`. Previously this only wrote to
  // localStorage; the DB write was missing, so reps' scorecard answers were
  // silently discarded on reload. Fixed 2026-05-11.
  const saveAnswer = useCallback(
    (questionKey: string, selectedOption: string | null, notes: string) => {
      const session = getSession();
      const who = session?.email ?? "unknown";
      const now = new Date().toISOString();
      setAnswers((prev) => {
        const oldOption = prev[questionKey]?.selectedOption ?? null;
        const next: AnswerStore = {
          ...prev,
          [questionKey]: {
            selectedOption,
            notes,
            updatedAt: now,
            updatedBy: who,
          },
        };
        appendAuditEntry(accountId, {
          questionKey,
          oldOption,
          newOption: selectedOption,
          notes,
          updatedAt: now,
          updatedBy: who,
        });
        return next;
      });

      // Persist to Supabase. account_answers is uniquely keyed on
      // (account_id, question_key); upsert with onConflict so re-answering
      // overwrites rather than inserting duplicates.
      const accountIdNum = parseInt(accountId, 10);
      if (!Number.isNaN(accountIdNum)) {
        supabase()
          .from("account_answers")
          .upsert(
            {
              account_id: accountIdNum,
              question_key: questionKey,
              selected_option: selectedOption,
              answer_notes: notes,
              updated_at: now,
            },
            { onConflict: "account_id,question_key" }
          )
          .then(({ error }) => {
            if (error) {
              console.error(
                "[saveAnswer] Supabase upsert failed",
                { accountId, questionKey, error: error.message }
              );
              window.dispatchEvent(
                new CustomEvent("aip:answer-save-error", {
                  detail: { accountId, questionKey, error: error.message },
                })
              );
            } else {
              window.dispatchEvent(
                new CustomEvent("aip:answer-saved-db", {
                  detail: { accountId, questionKey, selectedOption },
                })
              );
            }
          });
      } else {
        console.error(
          "[saveAnswer] accountId is not numeric, skipping Supabase write",
          { accountId, questionKey }
        );
      }

      // Cross-view sync event (Manager/Exec badges listen to this)
      window.dispatchEvent(
        new CustomEvent("aip:answer-saved", {
          detail: { accountId, questionKey, selectedOption, notes },
        })
      );
    },
    [accountId]
  );

  // Advance to next CP
  const handleNextCp = useCallback(() => {
    setSelectedCp((prev) => {
      if (prev === null) return 1;
      return prev < 10 ? prev + 1 : prev;
    });
  }, []);

  // W15: CP tape-recorder right panel state (readOnly mode only).
  // Replaces the OrbReadDrawer sheet and the broken zoom/pinch gesture system.
  const [tapeCp, setTapeCp] = useState<number | null>(null);

  // Select CP from either helix orb or legend click.
  // W15 FIX 3: In readOnly mode the helix is display-only — no navigation,
  // no drawer. CP selection feeds the tape-recorder button row and inline panel.
  // W45.3: also fires the optional onCpSelect callback for cross-panel sync.
  const handleSelectCp = useCallback((cp: number | null) => {
    setSelectedCp(cp);
    if (readOnly) {
      // Toggle: same CP button a second time deselects.
      setTapeCp((prev) => (prev === cp ? null : cp));
    }
    onCpSelect?.(cp);
  }, [readOnly, onCpSelect]);
  // W66.Q: light-theme tokens for the container chrome.
  // Helix SVG internals (DnaHelix3D) remain dark — that is locked canon.
  const LIGHT_BG      = "#F9FAFB";
  const LIGHT_CARD    = "#FFFFFF";
  const LIGHT_BORDER  = "#E5E7EB";
  // Full light palette retained for completeness; some tokens used in sub-components.
  void ("#111827"); // TEXT_PRIMARY
  void ("#374151"); // TEXT_SECONDARY
  void ("#6B7280"); // TEXT_MUTED

  return (
    <div
      style={{
        background: LIGHT_CARD,
        borderRadius: 12,
        border: `1px solid ${LIGHT_BORDER}`,
        overflow: "hidden",
        minHeight: 500,
      }}
    >
      {/* ══════════════════════════════════════════
          TOP BAR — DNA brand + composite + toggle
          W44.2: hidden when hideHeader=true (e.g. Tower embed)
         ══════════════════════════════════════════ */}
      {!hideHeader && (
      <div
        className="dna-v2-topbar"
        style={{
          padding: "20px 24px",
          borderBottom: "1px solid rgba(107,114,128,0.1)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        {/* Left: title block + composite */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 20 }}>
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#525258",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                marginBottom: 3,
              }}
            >
              {accountName ? `Account · ${accountName}` : "Account Intelligence"}
              {industry ? ` · ${industry}` : ""}
            </div>
            <h1
              className="dna-v2-h1"
              style={{
                margin: 0,
                fontSize: 32,
                fontWeight: 900,
                color: "#EFEFEE",
                lineHeight: 1,
                letterSpacing: "-0.02em",
              }}
            >
              Opportunity DNA
            </h1>
            <div style={{ fontSize: 11, color: "#525258", marginTop: 4, letterSpacing: "0.06em" }}>
              M-PACT qualification
            </div>
          </div>
          <CompositeRing score={compositeScore} />
        </div>

        {/* Helix is the canonical Opportunity DNA view — Tree toggle removed 2026-05-22 (D-075). */}
      </div>
      )}

      {/* ══════════════════════════════════════
          BODY — v2 layout: 3 columns side-by-side
            Left:   CP Legend (~220px, fixed)
            Center: Helix viewport (flex-1)
            Right:  Q&A drawer (~480px, slides in when CP selected)
         ══════════════════════════════════════ */}
      <div className="dna-v2-body" style={{ padding: "20px 24px" }}>
        <div className={`dna-v2-grid${readOnly ? " dna-v2-grid-readonly" : ""}`} style={{ display: "grid", gap: 16 }}>

            {/* ── Left: CP Legend ── */}
            <div
              className="dna-v2-legend"
              style={{
                background: LIGHT_BG,
                borderRadius: 12,
                border: `1px solid ${LIGHT_BORDER}`,
                padding: "14px 12px",
                maxHeight: 560,
                overflowY: "auto",
              }}
            >
              <CpLegend
                cpScores={cpScoreMap}
                selectedCp={selectedCp}
                onSelectCp={handleSelectCp}
              />
            </div>

            {/* ── Center: 3D Helix (visual surface — locked viewport) ── */}
            {/* W15 FIX 1: All gesture handlers removed. Viewport locked.
                No pinch-zoom, no pan, no free rotate. STAYS put after mount. */}
            <div
              className="dna-v2-helix"
              style={{
                height: 640,
                background: "#0A0A0C",
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.08)",
                position: "relative",
                touchAction: "none",
              }}
            >
              {/* ScoreHeader: only shown in edit mode — in readOnly the topbar
                  CompositeRing already surfaces the M-PACT composite score. */}
              {!readOnly && (
                <ScoreHeader
                  score={compositeScore}
                  dealName={accountName}
                  fixed={false}
                  noData={Object.keys(answers).length === 0}
                  evidenceCapCount={unevidencedAssumptionCount}
                />
              )}
              <DnaHelix3D
                cpScores={cpScoreMap}
                selectedCp={selectedCp}
                onSelectCp={readOnly ? () => {} : handleSelectCp}
                composite={compositeScore}
                autoTour
                accountId={accountId}
                hidePills={readOnly}
                helixOrientation={helixOrientation}
              />
              {/* W3-Tower: showTowerOverlay omitted — defaults to false. */}
            </div>
            {/* ── Right: CP read panel (readOnly) or Q&A (edit mode) ── */}
            {readOnly ? (
              // W15 FIX 4: CP read panel — populated by CP chip tap.
              // W66.Q Fix 5/10: light bg, viewport cap so panel never exceeds screen.
              <div
                className="dna-v2-qa"
                style={{
                  background: LIGHT_CARD,
                  borderRadius: 12,
                  border: `1px solid ${LIGHT_BORDER}`,
                  overflowY: "auto",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 360,
                  maxHeight: "calc(100vh - 220px)",
                }}
              >
                <CpReadPanel
                  selectedCp={selectedCp}
                  answers={answers}
                  cpScores={cpScores}
                  industry={industry}
                  onSaveAnswer={saveAnswer}
                  userId={getSession()?.email ?? "user"}
                />
              </div>
            ) : (
              <div
                className="dna-v2-qa"
                style={{
                  background: "#FFFFFF",
                  borderRadius: 12,
                  border: "1px solid #E5E5E7",
                  maxHeight: 560,
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                <QaPanel
                  selectedCp={selectedCp}
                  answers={answers}
                  industry={industry}
                  onAnswer={saveAnswer}
                  onNextCp={handleNextCp}
                  cpScores={cpScores}
                  onClose={() => handleSelectCp(null)}
                  accountId={accountId}
                  readOnly={readOnly}
                  onEvidenceSaved={(questionKey, evidenceType, isAssumption) => {
                    setAnswers((prev) => ({
                      ...prev,
                      [questionKey]: {
                        ...prev[questionKey],
                        evidenceType: evidenceType as "text" | "url" | "file" | null,
                        isAssumption,
                      },
                    }));
                  }}
                />
              </div>
            )}

          </div>

          {/* W66.Q Fix 9: CpTapeRow (duplicate M-PACT CHECKPOINTS bottom grid) removed.
               The left legend column already provides CP selection. Two CP selectors
               on one panel was redundant. Legend is the single source. */}
      </div>

      {/* W15: readOnly mode shows CP read panel as third column
          W44.2: Q&A drawer max-width constrained on iPad portrait so CHECKPOINTS column is not clipped */}
      <style>{`
        @media (min-width: 1024px) {
          .dna-v2-grid {
            grid-template-columns: 220px minmax(0, 1fr) 380px !important;
            grid-template-areas: "legend helix qa" !important;
          }
          /* W15: readOnly shows legend + helix + CP read panel */
          .dna-v2-grid-readonly {
            grid-template-columns: 180px minmax(0, 1fr) 320px !important;
            grid-template-areas: "legend helix qa" !important;
          }
          .dna-v2-legend { grid-area: legend; }
          .dna-v2-helix  { grid-area: helix; }
          .dna-v2-qa     { grid-area: qa; }
        }
        /* W44.2: iPad portrait range (834-1023px). Three columns but Q&A drawer
           capped at 260px so the CHECKPOINTS legend column is not clipped. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .dna-v2-grid {
            grid-template-columns: 180px minmax(0, 1fr) 260px !important;
            grid-template-areas: "legend helix qa" !important;
            display: grid !important;
          }
          .dna-v2-grid-readonly {
            grid-template-columns: 160px minmax(0, 1fr) 220px !important;
            grid-template-areas: "legend helix qa" !important;
            display: grid !important;
          }
          .dna-v2-legend { grid-area: legend; max-height: none !important; }
          .dna-v2-helix  { grid-area: helix; height: 460px !important; }
          .dna-v2-qa     { grid-area: qa; max-height: 560px !important; }
          .dna-v2-grid-readonly .dna-v2-legend { display: block !important; }
          .dna-v2-grid-readonly .dna-v2-qa { display: block !important; }
        }
        @media (max-width: 767px) {
          .dna-v2-grid { grid-template-columns: 1fr !important; }
          .dna-v2-helix { height: 460px !important; }
          .dna-v2-legend { max-height: 280px !important; }
          .dna-v2-qa { max-height: 460px !important; }
          /* readOnly: hide legend and CP read panel columns on small mobile.
             The CP tape row below the helix provides all navigation. */
          .dna-v2-grid-readonly .dna-v2-legend { display: none !important; }
          .dna-v2-grid-readonly .dna-v2-qa { display: none !important; }
        }
        @media (max-width: 640px) {
          .dna-v2-body { padding: 12px 12px !important; }
          .dna-v2-grid { gap: 12px !important; }
          .dna-v2-helix { height: 320px !important; min-height: 0 !important; }
          .dna-v2-legend { max-height: 260px !important; padding: 12px 10px !important; }
          .dna-v2-qa { max-height: 420px !important; }
          .dna-v2-topbar { padding: 14px 12px !important; gap: 10px !important; }
          .dna-v2-h1 { font-size: 22px !important; }
        }
      `}</style>
    </div>
  );
}
